import type { ThresholdPoint } from "@modules/energy-manager/types/alarme"
import {
    ALARM_THRESHOLD_DEFAULT_END_NIGHT,
    ALARM_THRESHOLD_DEFAULT_START_NIGHT,
    getIsLegacyNightHour,
} from "@modules/energy-manager/utils/alarmThresholdFormLegacy"

/**
 * Clé de comparaison des triplets consigne / limites.
 */
function tripletKey(p: ThresholdPoint): string {
    return `${p.consigne}|${p.limhaute}|${p.limbasse}`
}

/** Part minimale d’heures alignées sur le mode dans une bande jour ou nuit (ex. lundi 0 h atypique). */
const INFER_NIGHT_BAND_MIN_FRACTION = 0.65

/** Triplet le plus fréquent dans une liste (profil type « jour ouvré » si lun–ven diffèrent un peu). */
function modeTripletKey(keys: string[]): string {
    const counts = new Map<string, number>()
    for (const k of keys) {
        counts.set(k, (counts.get(k) ?? 0) + 1)
    }
    let best = keys[0]
    let max = 0
    for (const [k, c] of counts) {
        if (c > max) {
            max = c
            best = k
        }
    }
    return best
}

/**
 * Infère si le profil quotidien (7 j) distingue semaine et week-end (indices legacy 0=dim … 6=sam).
 */
export function inferDistinguishWeekend7(pts: ThresholdPoint[]): boolean {
    if (pts.length !== 7) return false
    const monFriKeys = [1, 2, 3, 4, 5].map((i) => tripletKey(pts[i]))
    if (new Set(monFriKeys).size > 1) return true
    const sun = tripletKey(pts[0])
    const sat = tripletKey(pts[6])
    if (sun !== sat) return true
    const weekKey = monFriKeys[0]
    return sun !== weekKey
}

/**
 * Pour chaque heure civile, compare le mode lun–ven au dimanche et samedi.
 * (Les lun–ven ne sont pas forcément identiques entre eux — ex. lundi ≠ mardi — sans que ce soit un « week-end ».)
 */
export function inferDistinguishWeekend168(pts: ThresholdPoint[]): boolean {
    if (pts.length !== 168) return false
    for (let hour = 0; hour < 24; hour++) {
        const weekKeys = [1, 2, 3, 4, 5].map((d) =>
            tripletKey(pts[d * 24 + hour])
        )
        const modeWeek = modeTripletKey(weekKeys)
        const sunK = tripletKey(pts[0 * 24 + hour])
        const satK = tripletKey(pts[6 * 24 + hour])
        if (sunK !== satK) return true
        if (modeWeek !== sunK || modeWeek !== satK) return true
    }
    return false
}

export type InferredNight24 = {
    nightEnabled: boolean
    startNight: number
    endNight: number
    dayPoint: ThresholdPoint
    nightPoint: ThresholdPoint
}

type NightWindowCandidate = {
    score: number
    startNight: number
    endNight: number
    dayMode: string
    nightMode: string
}

/**
 * À partir d’une tranche 24 h (index = heure), détecte jour vs nuit et la fenêtre legacy compatible.
 * Utilise le triplet majoritaire par bande (jour / nuit) pour tolérer une ou quelques heures atypiques.
 */
export function inferNightFrom24Points(
    slice: ThresholdPoint[]
): InferredNight24 | null {
    if (slice.length !== 24) return null
    const keys = slice.map(tripletKey)
    const unique = new Set(keys)
    const noon = slice[12]
    if (unique.size === 1) {
        return {
            nightEnabled: false,
            startNight: ALARM_THRESHOLD_DEFAULT_START_NIGHT,
            endNight: ALARM_THRESHOLD_DEFAULT_END_NIGHT,
            dayPoint: noon,
            nightPoint: noon,
        }
    }

    let best: NightWindowCandidate | null = null

    for (let startNight = 17; startNight <= 23; startNight++) {
        for (let endNight = 4; endNight <= 9; endNight++) {
            const dayKeys: string[] = []
            const nightKeys: string[] = []
            for (let h = 0; h < 24; h++) {
                if (getIsLegacyNightHour(h, startNight, endNight)) {
                    nightKeys.push(keys[h])
                } else {
                    dayKeys.push(keys[h])
                }
            }
            if (dayKeys.length === 0 || nightKeys.length === 0) continue

            const dayMode = modeTripletKey(dayKeys)
            const nightMode = modeTripletKey(nightKeys)
            if (dayMode === nightMode) continue

            const dayFrac =
                dayKeys.filter((k) => k === dayMode).length / dayKeys.length
            const nightFrac =
                nightKeys.filter((k) => k === nightMode).length /
                nightKeys.length

            if (
                dayFrac < INFER_NIGHT_BAND_MIN_FRACTION ||
                nightFrac < INFER_NIGHT_BAND_MIN_FRACTION
            ) {
                continue
            }

            const score = dayFrac * nightFrac
            const cand: NightWindowCandidate = {
                score,
                startNight,
                endNight,
                dayMode,
                nightMode,
            }

            if (!best) {
                best = cand
                continue
            }

            const isDefault17 =
                cand.startNight === ALARM_THRESHOLD_DEFAULT_START_NIGHT &&
                cand.endNight === ALARM_THRESHOLD_DEFAULT_END_NIGHT
            const bestIsDefault17 =
                best.startNight === ALARM_THRESHOLD_DEFAULT_START_NIGHT &&
                best.endNight === ALARM_THRESHOLD_DEFAULT_END_NIGHT

            if (cand.score > best.score) {
                best = cand
            } else if (cand.score === best.score) {
                if (isDefault17 && !bestIsDefault17) {
                    best = cand
                }
            }
        }
    }

    if (best) {
        const b = best
        const dayPoint =
            slice.find(
                (p, h) =>
                    !getIsLegacyNightHour(h, b.startNight, b.endNight) &&
                    tripletKey(p) === b.dayMode
            ) ?? noon
        const nightPoint =
            slice.find(
                (p, h) =>
                    getIsLegacyNightHour(h, b.startNight, b.endNight) &&
                    tripletKey(p) === b.nightMode
            ) ?? dayPoint
        return {
            nightEnabled: true,
            startNight: b.startNight,
            endNight: b.endNight,
            dayPoint,
            nightPoint,
        }
    }

    return {
        nightEnabled: false,
        startNight: ALARM_THRESHOLD_DEFAULT_START_NIGHT,
        endNight: ALARM_THRESHOLD_DEFAULT_END_NIGHT,
        dayPoint: noon,
        nightPoint: noon,
    }
}

/**
 * État de toggles / bornes nuit à appliquer avant `syncScalarsFromPoints`.
 */
export type InferredAlarmThresholdToggles = {
    isDistinguishWeekend: boolean
    isUniformNight: boolean
    uniformStartNight: number
    uniformEndNight: number
    weekIsNight: boolean
    weekStartNight: number
    weekEndNight: number
    weekendIsNight: boolean
    weekendStartNight: number
    weekendEndNight: number
}

/**
 * Déduit les options du formulaire (semaine / week-end, jour / nuit) depuis la série API.
 */
export function inferAlarmThresholdTogglesFromPoints(
    pts: ThresholdPoint[]
): InferredAlarmThresholdToggles {
    const defaults: InferredAlarmThresholdToggles = {
        isDistinguishWeekend: false,
        isUniformNight: false,
        uniformStartNight: ALARM_THRESHOLD_DEFAULT_START_NIGHT,
        uniformEndNight: ALARM_THRESHOLD_DEFAULT_END_NIGHT,
        weekIsNight: false,
        weekStartNight: ALARM_THRESHOLD_DEFAULT_START_NIGHT,
        weekEndNight: ALARM_THRESHOLD_DEFAULT_END_NIGHT,
        weekendIsNight: false,
        weekendStartNight: ALARM_THRESHOLD_DEFAULT_START_NIGHT,
        weekendEndNight: ALARM_THRESHOLD_DEFAULT_END_NIGHT,
    }

    if (pts.length === 7) {
        defaults.isDistinguishWeekend = inferDistinguishWeekend7(pts)
        return defaults
    }

    if (pts.length !== 168) {
        return defaults
    }

    defaults.isDistinguishWeekend = inferDistinguishWeekend168(pts)

    /** Mercredi : tranche jour ouvré typique (évite lundi 0 h hors motif lun–ven). */
    const wednesdaySlice = pts.slice(72, 96)
    const sundaySlice = pts.slice(0, 24)
    const saturdaySlice = pts.slice(144, 168)

    const inferWeek = inferNightFrom24Points(wednesdaySlice)
    const inferSun = inferNightFrom24Points(sundaySlice)
    const inferSat =
        saturdaySlice.length === 24
            ? inferNightFrom24Points(saturdaySlice)
            : null

    if (!defaults.isDistinguishWeekend) {
        const u = inferWeek
        if (u) {
            defaults.isUniformNight = u.nightEnabled
            defaults.uniformStartNight = u.startNight
            defaults.uniformEndNight = u.endNight
        }
        return defaults
    }

    if (inferWeek) {
        defaults.weekIsNight = inferWeek.nightEnabled
        defaults.weekStartNight = inferWeek.startNight
        defaults.weekEndNight = inferWeek.endNight
    }

    const weekendInfer =
        inferSun &&
        inferSat &&
        inferSun.nightEnabled === inferSat.nightEnabled &&
        inferSun.startNight === inferSat.startNight &&
        inferSun.endNight === inferSat.endNight &&
        tripletKey(inferSun.dayPoint) === tripletKey(inferSat.dayPoint) &&
        tripletKey(inferSun.nightPoint) === tripletKey(inferSat.nightPoint)
            ? inferSun
            : (inferSun ?? inferSat)

    if (weekendInfer) {
        defaults.weekendIsNight = weekendInfer.nightEnabled
        defaults.weekendStartNight = weekendInfer.startNight
        defaults.weekendEndNight = weekendInfer.endNight
    }

    return defaults
}
